import { formatDateDayMonthYear } from '../../../utils';
import { useDataFetch } from '../../../hooks/use-data-fetch';
import { useUser } from '../../../context/UserContext';
import { useMultiCurrencyBonus, CurrencyTotals, ActiveCurrency, DatePeriod } from '../../../hooks/use-multi-currency-bonus';

interface MonthPeriod extends DatePeriod { }

interface Level {
  _id: string;
  name: string;
  porcentage: number;
  state: boolean;
  id: string;
}

interface PointsData {
  totalLoyaltyPointsByLevel: Record<string, CurrencyTotals>;
  monthPeriod: MonthPeriod;
  levelOneActive: number;
}

interface LoyaltyBonusData {
  pointsData: PointsData;
  levels: Level[];
  levelOneActive: number;
}

interface LoyaltyBonusResponse {
  message: string;
  data: LoyaltyBonusData;
  errors: null | any;
}

interface LevelCalculation {
  level: number;
  name: string;
  points: number;
  pointsByCurrency: CurrencyTotals;
  percentage: number;
  pointValue: number;
  currency: string;
  calculatedValue: number;
  calculatedValueByCurrency: CurrencyTotals;
  isLocked: boolean; // Indica si el nivel está bloqueado
}



export const useLoyaltyBonus = () => {
  const { getData } = useDataFetch();
  const { userData } = useUser();

  // Use the generic multi-currency bonus hook
  const {
    getPointValueByCurrency,
    calculateProgressPercentage,
    calculateDaysRemaining,
    isCurrentPeriod,
    countriesLoading,
    userCurrency,
    userPointValue
  } = useMultiCurrencyBonus();

  const {
    data: loyaltyData,
    isLoading,
    error,
    refetch
  } = getData(
    ['loyalty-bonus-monthly'],
    '/order/calculate/monthly/loyalty_points',
    {
      enabled: true
    }
  );

  // Parse and format the data
  const parsedData: LoyaltyBonusResponse | null = loyaltyData || null;

  const pointValue = userPointValue;
  const currency = userCurrency;

  // Get currencies with points > 0 across unlocked levels only
  const getActiveCurrencies = (): ActiveCurrency[] => {
    const calculations = getLevelCalculations();
    const currencyTotals: Record<string, { totalPoints: number; totalValue: number }> = {};

    // Sum points and values across unlocked levels only for each currency
    calculations
      .filter(level => !level.isLocked) // Solo incluir niveles no bloqueados
      .forEach(level => {
        Object.entries(level.pointsByCurrency).forEach(([currency, points]) => {
          if (!currencyTotals[currency]) {
            currencyTotals[currency] = { totalPoints: 0, totalValue: 0 };
          }
          currencyTotals[currency].totalPoints += points;
          currencyTotals[currency].totalValue += level.calculatedValueByCurrency[currency as keyof CurrencyTotals];
        });
      });

    // Filter currencies with points > 0
    const activeCurrencies = Object.entries(currencyTotals)
      .filter(([_, totals]) => totals.totalPoints > 0)
      .map(([currency, totals]) => ({
        currency,
        totalPoints: totals.totalPoints,
        totalValue: totals.totalValue
      }));

    // If no active currencies, return user's default currency with 0 values
    if (activeCurrencies.length === 0) {
      return [{
        currency,
        totalPoints: 0,
        totalValue: 0
      }];
    }

    return activeCurrencies;
  };

  // Calculate loyalty values for each level
  const getLevelCalculations = (): LevelCalculation[] => {
    if (!parsedData?.data) return [];

    const { pointsData, levels } = parsedData.data;
    const hasEnoughActiveUsers = pointsData.levelOneActive >= 3;

    return levels.map((level: Level) => {
      const levelNumber = parseInt(level.name);
      const pointsByCurrency = pointsData.totalLoyaltyPointsByLevel[level.name] || {
        COP: 0,
        USDT: 0,
        EUR: 0,
        USD: 0
      };

      // Determinar si el nivel está bloqueado
      const isLocked = !hasEnoughActiveUsers && levelNumber >= 4;

      // Calculate total points for this level across all currencies
      const totalPoints = Object.values(pointsByCurrency).reduce((sum, points) => sum + points, 0);

      const percentage = level.porcentage / 100; // Convert percentage to decimal
      const calculatedValue = totalPoints * percentage * pointValue;

      // Calculate values by currency using correct point value for each currency
      const calculatedValueByCurrency: CurrencyTotals = {
        COP: pointsByCurrency.COP * percentage * getPointValueByCurrency('COP'),
        USDT: pointsByCurrency.USDT * percentage * getPointValueByCurrency('USDT'),
        EUR: pointsByCurrency.EUR * percentage * getPointValueByCurrency('EUR'),
        USD: pointsByCurrency.USD * percentage * getPointValueByCurrency('USD')
      };

      return {
        level: levelNumber,
        name: `Nivel ${levelNumber}`,
        points: totalPoints,
        pointsByCurrency,
        percentage: level.porcentage,
        pointValue,
        currency,
        calculatedValue,
        calculatedValueByCurrency,
        isLocked
      };
    });
  };

  const getTotalPoints = (): number => {
    // Sum points only from unlocked levels
    return getLevelCalculations()
      .filter(level => !level.isLocked)
      .reduce((sum, level) => sum + level.points, 0);
  };

  const getTotalCalculatedValue = (): number => {
    // Sum calculated values only from unlocked levels
    return getLevelCalculations()
      .filter(level => !level.isLocked)
      .reduce((sum, level) => sum + level.calculatedValue, 0);
  };

  const getProgressPercentage = (): number => {
    if (!parsedData?.data?.pointsData?.monthPeriod) return 0;
    return calculateProgressPercentage(parsedData.data.pointsData.monthPeriod);
  };

  const getDaysRemaining = (): number => {
    if (!parsedData?.data?.pointsData?.monthPeriod) return 0;
    return calculateDaysRemaining(parsedData.data.pointsData.monthPeriod);
  };

  return {
    // Raw data
    loyaltyData: parsedData?.data || null,
    isLoading: isLoading || countriesLoading,
    error,
    refetch,

    // Computed values
    levelCalculations: getLevelCalculations(),
    totalPoints: getTotalPoints(),
    totalCalculatedValue: getTotalCalculatedValue(),
    activeCurrencies: getActiveCurrencies(),
    currency,
    pointValue,

    // Period information
    monthPeriod: parsedData?.data?.pointsData?.monthPeriod || null,
    startDate: parsedData?.data?.pointsData?.monthPeriod ?
      formatDateDayMonthYear(parsedData.data.pointsData.monthPeriod.start) : '',
    endDate: parsedData?.data?.pointsData?.monthPeriod ?
      formatDateDayMonthYear(parsedData.data.pointsData.monthPeriod.end) : '',
    progressPercentage: getProgressPercentage(),
    daysRemaining: getDaysRemaining(),

    // Status
    isCurrentMonth: parsedData?.data?.pointsData?.monthPeriod ?
      isCurrentPeriod(parsedData.data.pointsData.monthPeriod) : false,

    // Feature flags
    loyaltyEnabled: userData?.network?.loyalty_status ?? false,
  };
};
